import { BriefResponseDto } from '@/app/common/general-types/briefs.dto';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { Component, inject, input, output, signal } from '@angular/core';
import { StarRating } from '../star-rating/star-rating';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { CreateReviewFancyDto } from '@/app/common/general-types/reviews.dto';
import { FormsModule } from '@angular/forms';
import { ReviewsFancyService } from '@/app/services/reviews-fancy/reviews-fancy.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { ViewportOverlayDirective } from '@/app/common/directives/viewport-overlay/viewport-overlay.directive';

@Component({
  selector: 'vs-review-modal',
  imports: [StarRating, FormsModule],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './review-modal.html',
  styleUrl: './review-modal.scss',
})
export class ReviewModal {

  creator = input<CreatorResponseDto>();
  brief = input<BriefResponseDto>();
  briefApplicationId = input<string>();

  closeReviewModal = output<void>();

  /** Rating Variables */
  communicationRating = signal<number>(5);
  workQualityRating = signal<number>(5);
  professionalismRating = signal<number>(5);
  timeManagementRating = signal<number>(5);

  review: string = '';

  wouldRecommend = signal<boolean | undefined>(true);

  isSubmittingReview = signal<boolean>(false);

  /** Services */
  private firebaseService = inject(FirebaseService);
  private reviewsFancyService = inject(ReviewsFancyService);
  private toastNotificationService = inject(ToastNotificationService);
  /** Services - END*/

  setWouldRecommend(value: boolean) {
    this.wouldRecommend.set(value);
  }

  updateRating(i: number, type: 'communication' | 'workQuality' | 'professionalism' | 'timeManagement') {
    switch (type) {
      case 'communication':
        this.communicationRating.set(i);
        break;
      case 'workQuality':
        this.workQualityRating.set(i);
        if (this.workQualityRating() > 3) {
          this.wouldRecommend.set(true);
        }
        if (this.workQualityRating() < 3) {
          this.wouldRecommend.set(false);
        }
        break;
      case 'professionalism':
        this.professionalismRating.set(i);
        if (this.professionalismRating() > 3) {
          this.wouldRecommend.set(true);
        }
        if (this.professionalismRating() < 3) {
          this.wouldRecommend.set(false);
        }
        break;
      case 'timeManagement':
        this.timeManagementRating.set(i);
        break;
    }
  }

  submitReview() {
    console.log('submitReview');
    if (this.creator() && this.briefApplicationId()) {
      this.isSubmittingReview.set(true);
      let newReview: CreateReviewFancyDto = {
        creatorId: this.creator()?.blazeFirebaseUid || '',
        workQualityRating: this.workQualityRating(),
        communicationRating: this.communicationRating(),
        professionalismRating: this.professionalismRating(),
        timeManagementRating: this.timeManagementRating(),
        wouldRecommend: this.wouldRecommend() !== undefined ? this.wouldRecommend()! : false,
        review: this.review ? this.review : 'Thank you!',
        privateNote: 'No private comment...',
        reviewedBy: this.firebaseService.userId! ? this.firebaseService.userId! : 'unknown',
        createdDate: new Date(),
        applicationId: this.briefApplicationId()!,
        unHired: false
      }

      console.log('newReview: ', newReview);

      this.reviewsFancyService.createReview(newReview).subscribe((response: any) => {
        console.log('response: ', response);
        this.isSubmittingReview.set(false);
        this.toastNotificationService.alert({ title: 'Review submitted successfully', message: 'Review submitted successfully', type: 'success', icon: 'success' });
        this.closeReviewModal.emit(response);
      }, (error: any) => {
        console.error('error: ', error);
        this.isSubmittingReview.set(false);
        this.toastNotificationService.alert({ title: 'Error submitting review', message: 'Error submitting review', type: 'error', icon: 'error' });
      });
    }
  }

  /** Shared interaction chrome: keyboard focus + motion (WCAG / touch-friendly). */
  private recommendToggleBase(): string {
    return [
      'cursor-pointer',
      'transition-colors duration-200',
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
      'focus-visible:outline-[color:var(--ring)]',
    ].join(' ');
  }

  yesReccomendClass(): string {
    const base = this.recommendToggleBase();
    if (this.wouldRecommend() === true) {
      return [
        base,
        'bg-green-600 text-white',
        'ring-1 ring-inset ring-green-800/30',
        'dark:bg-green-500 dark:text-white dark:ring-green-950/35',
      ].join(' ');
    }
    return [
      base,
      'bg-transparent text-primary',
      'hover:bg-green-600/10 dark:hover:bg-green-400/15',
      'active:bg-green-600/15 dark:active:bg-green-400/25',
    ].join(' ');
  }

  noReccomendClass(): string {
    const base = this.recommendToggleBase();
    if (this.wouldRecommend() === false) {
      return [
        base,
        'bg-red-600 text-white',
        'ring-1 ring-inset ring-red-800/30',
        'dark:bg-red-500 dark:text-white dark:ring-red-950/35',
      ].join(' ');
    }
    return [
      base,
      'bg-transparent text-primary',
      'hover:bg-red-600/10 dark:hover:bg-red-400/15',
      'active:bg-red-600/15 dark:active:bg-red-400/25',
    ].join(' ');
  }

  getCommentPlaceholder() {
    return `What was it like working with ${this.creator()?.firstName}? What stood out - this is useful for the next hirer to know.`;
  }
}
